/**
 * Info Tooltip Manager
 * Handles hover and click interactions for info icons with tooltips
 */

class InfoTooltipManager {
    constructor() {
        this.activeTooltip = null;
        this.hoverTimeout = null;
        this.init();
    }

    init() {
        // Wait for DOM and i18n to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    setup() {
        // Add event listeners to all info icons
        this.attachEventListeners();
        
        // Close tooltip when clicking outside
        document.addEventListener('click', (e) => this.handleOutsideClick(e));
        
        // Close tooltip on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.activeTooltip) {
                this.hideTooltip();
            }
        });
        
        // Reposition tooltips on window resize
        window.addEventListener('resize', () => {
            if (this.activeTooltip) {
                this.positionTooltip(this.activeTooltip, this.activeTooltip._triggerElement);
            }
        });
    }

    attachEventListeners() {
        const infoIcons = document.querySelectorAll('.info-icon');
        
        infoIcons.forEach(icon => {
            // Mouse enter - show tooltip after delay
            icon.addEventListener('mouseenter', (e) => this.handleMouseEnter(e, icon));
            
            // Mouse leave - hide tooltip after delay
            icon.addEventListener('mouseleave', (e) => this.handleMouseLeave(e, icon));
            
            // Click - toggle tooltip (for mobile and to keep it open)
            icon.addEventListener('click', (e) => this.handleClick(e, icon));
        });
    }

    handleMouseEnter(e, icon) {
        e.stopPropagation();
        
        // Don't show hover tooltip if there's already an active (clicked) tooltip
        if (this.activeTooltip && this.activeTooltip._isActive) {
            return;
        }
        
        // Clear any pending hide timeout
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
        }
        
        // Show tooltip after a short delay
        this.hoverTimeout = setTimeout(() => {
            this.showTooltip(icon, false);
        }, 300);
    }

    handleMouseLeave(e, icon) {
        // Clear hover timeout
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
        }
        
        // If tooltip is not actively clicked, hide it after delay
        if (this.activeTooltip && !this.activeTooltip._isActive) {
            this.hoverTimeout = setTimeout(() => {
                this.hideTooltip();
            }, 200);
        }
    }

    handleClick(e, icon) {
        e.preventDefault();
        e.stopPropagation();
        
        // If clicking the same icon, toggle tooltip
        if (this.activeTooltip && this.activeTooltip._triggerElement === icon) {
            if (this.activeTooltip._isActive) {
                this.hideTooltip();
            } else {
                this.activeTooltip._isActive = true;
                this.activeTooltip.classList.add('active');
            }
        } else {
            // Show new tooltip and mark as active (clicked)
            this.showTooltip(icon, true);
        }
    }

    handleOutsideClick(e) {
        // Don't auto-close on outside click - only close button works
        // This prevents accidental closes on mobile
        return;
    }

    showTooltip(icon, isActive = false) {
        // Hide any existing tooltip
        this.hideTooltip();
        
        // Get the info key from data attribute
        const infoKey = icon.getAttribute('data-info-key');
        if (!infoKey) {
            console.warn('Info icon missing data-info-key attribute', icon);
            return;
        }
        
        // Get translated content
        const content = window.i18n ? window.i18n.t(infoKey) : infoKey;
        
        // Create tooltip element
        const tooltip = this.createTooltip(content);
        tooltip._triggerElement = icon;
        tooltip._isActive = isActive;
        
        // Add to DOM
        document.body.appendChild(tooltip);
        
        // Position tooltip
        this.positionTooltip(tooltip, icon);
        
        // Show tooltip with animation
        requestAnimationFrame(() => {
            tooltip.classList.add('visible');
            if (isActive) {
                tooltip.classList.add('active');
            }
        });
        
        // Store reference
        this.activeTooltip = tooltip;
    }

    createTooltip(content) {
        const tooltip = document.createElement('div');
        tooltip.className = 'info-tooltip';
        
        // Create content container
        const contentDiv = document.createElement('div');
        contentDiv.className = 'info-tooltip-content';
        contentDiv.textContent = content;
        
        // Create close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'info-tooltip-close';
        closeBtn.setAttribute('aria-label', 'Sluiten');
        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.hideTooltip();
        });
        
        tooltip.appendChild(contentDiv);
        tooltip.appendChild(closeBtn);
        
        // Prevent clicks inside tooltip from closing it
        tooltip.addEventListener('click', (e) => e.stopPropagation());
        
        return tooltip;
    }

    positionTooltip(tooltip, triggerElement) {
        const iconRect = triggerElement.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 10;
        
        let position = 'bottom';
        let top, left;
        
        // Try positioning below the icon first (preferred)
        top = iconRect.bottom + padding;
        left = iconRect.left;
        
        // Check if tooltip would go below viewport
        if (top + tooltipRect.height + padding > viewportHeight) {
            // Try positioning above
            position = 'top';
            top = iconRect.top - tooltipRect.height - padding;
            
            // If still doesn't fit, position it at top of viewport
            if (top < padding) {
                position = 'bottom';
                top = iconRect.bottom + padding;
                
                // If really no space, position next to icon
                if (top + tooltipRect.height > viewportHeight - padding) {
                    position = 'right';
                    top = iconRect.top;
                    left = iconRect.right + padding;
                    
                    // Check if it fits on the right
                    if (left + tooltipRect.width > viewportWidth - padding) {
                        position = 'left';
                        left = iconRect.left - tooltipRect.width - padding;
                    }
                }
            }
        }
        
        // Adjust horizontal position to stay in viewport
        if (position === 'top' || position === 'bottom') {
            // Center align with icon, but ensure it stays in viewport
            left = iconRect.left + (iconRect.width / 2) - (tooltipRect.width / 2);
            
            if (left < padding) {
                left = padding;
            } else if (left + tooltipRect.width > viewportWidth - padding) {
                left = viewportWidth - tooltipRect.width - padding;
            }
        }
        
        // Adjust vertical position to stay in viewport
        if (position === 'left' || position === 'right') {
            if (top + tooltipRect.height > viewportHeight - padding) {
                top = viewportHeight - tooltipRect.height - padding;
            }
            if (top < padding) {
                top = padding;
            }
        }
        
        // Apply position
        tooltip.style.position = 'fixed';
        tooltip.style.top = `${top}px`;
        tooltip.style.left = `${left}px`;
        
        // Update tooltip position class for arrow
        tooltip.classList.remove('position-top', 'position-bottom', 'position-left', 'position-right');
        tooltip.classList.add(`position-${position}`);
    }

    hideTooltip() {
        if (this.activeTooltip) {
            const tooltipToRemove = this.activeTooltip;
            this.activeTooltip = null; // Clear reference immediately
            
            tooltipToRemove.classList.remove('visible', 'active');
            
            // Remove from DOM after animation
            setTimeout(() => {
                if (tooltipToRemove && tooltipToRemove.parentNode) {
                    tooltipToRemove.parentNode.removeChild(tooltipToRemove);
                }
            }, 200);
        }
        
        // Clear any pending timeouts
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
        }
    }

    // Public method to refresh event listeners (useful after dynamic content updates)
    refresh() {
        this.attachEventListeners();
    }
}

// Initialize tooltip manager when script loads
window.infoTooltipManager = new InfoTooltipManager();

// Refresh tooltips after i18n translation updates
if (window.i18n) {
    const originalRetranslate = window.i18n.retranslate;
    window.i18n.retranslate = function() {
        originalRetranslate.call(window.i18n);
        if (window.infoTooltipManager) {
            window.infoTooltipManager.refresh();
        }
    };
}
